import { Component, DestroyRef, inject, Inject, Injector, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  InstanceResponseDto,
  SnapshotResponseDto,
} from '@cloud-twin-v2/generated/simulation.generated.client';
import { WidgetSettingsToken } from '@common-modules/dynamic-layout/dynamic-layout.tokens';
import { SetValueAction } from '@common-modules/dynamic-layout/state/generic/generic.actions';
import { GetValueSelector } from '@common-modules/dynamic-layout/state/generic/generic.selectors';
import { BaseDynamicWidgetComponent } from '@common-modules/redux/components/base-dynamic-widget.component';
import { StateWidgetSettings } from '@common-modules/redux/models/state-widget-settings';
import { ReduxStateService } from '@common-modules/redux/redux-state.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, Observable } from 'rxjs';
import { ModelVersionGridComponent } from '../model-version-grid/model-version-grid.component';
import { ModelStateFields } from '../models/model-state-fields';
import { ModelVersionSharedService } from '../services/model-version-shared.service';

const COMPONENT_SELECTOR = 'tc-model-version-grid-widget';

export const MODEL_VERSION_GRID_WIDGET_INSTANCE = COMPONENT_SELECTOR + '#1';

@UntilDestroy()
@Component({
  selector: COMPONENT_SELECTOR,
  templateUrl: './model-version-grid-widget.component.html',
  styleUrl: './model-version-grid-widget.component.scss',
  providers: [ReduxStateService],
  standalone: false,
})
export class ModelVersionGridWidgetComponent extends BaseDynamicWidgetComponent implements OnInit {
  @ViewChild('modelVersionGrid') modelVersionGrid: ModelVersionGridComponent;
  private readonly _destroyRef = inject(DestroyRef);

  constructor(
    injector: Injector,
    @Inject(WidgetSettingsToken) widgetSettings: StateWidgetSettings,
    private readonly _modelVersionSharedService: ModelVersionSharedService
  ) {
    super(injector, widgetSettings);

    this.pageId = widgetSettings.page;
  }

  get componentName(): string {
    return 'ModelVersionGridWidgetComponent';
  }

  pageId: string;
  instanceElement: InstanceResponseDto;

  onWidgetInit(): void {
    this.listenState(ModelStateFields.selectedModel)
      .pipe(filter((selectedInstance: InstanceResponseDto) => !!selectedInstance))
      .subscribe((selectedInstance: InstanceResponseDto) => {
        this._modelVersionSharedService.updateSelectInstance(selectedInstance.instanceId);
        this.instanceElement = selectedInstance;
      });

    this._modelVersionSharedService.instanceToDelete$
      .pipe(
        filter((instanceId) => !!instanceId),
        untilDestroyed(this)
      )
      .subscribe(() => {
        this.reloadItemsGrid();
      });

    this._modelVersionSharedService.snapshotsIdsToDelete$
      .pipe(
        filter((snapshotsIds) => !!snapshotsIds && snapshotsIds.length > 0),
        untilDestroyed(this)
      )
      .subscribe((snapshotsIds: string[]) => {
        this.snapshotsToDelete(snapshotsIds);
      });

    this._modelVersionSharedService.snapshotToUpdate$
      .pipe(
        filter((instanceId) => !!instanceId),
        untilDestroyed(this)
      )
      .subscribe((update) => {
        const { snapshotId, scenarioIds } = update;
        this.updateScenariosOfSnapshot(snapshotId, scenarioIds);
      });
  }

  reloadItemsGrid(): void {
    this.modelVersionGrid?.reloadItemsGrid();
  }

  updateScenariosOfSnapshot(snapshotId: string, scenarioIds: string[]) {
    this.modelVersionGrid?.updateScenariosOfSnapshot(snapshotId, scenarioIds);
  }

  snapshotsToDelete(scenarioIds: string[]): void {
    this.modelVersionGrid?.snapshotsToDelete(scenarioIds);
  }

  onSelectedSnapshot(snapshot: SnapshotResponseDto): void {
    this._state.dispatch(
      new SetValueAction({
        fieldName: ModelStateFields.selectedSnapshot,
        value: snapshot,
      })
    );
  }

  onSelectedSnapshots(snapshots: SnapshotResponseDto[]): void {
    this._modelVersionSharedService.updateSelect(snapshots);
  }

  private listenState(fieldName: string): Observable<any> {
    console.log('fieldName', fieldName);
    return this._state
      .select(
        new GetValueSelector({
          fieldName,
        })
      )
      .pipe(takeUntilDestroyed(this._destroyRef));
  }
}
